import { useState } from 'react'

import { useLocale, useTranslations } from 'next-intl'

import classNames from 'classnames'

import Button, { ButtonVariants } from '~/components/atoms/Button/Button'
import Container, { gutterTypes } from '~/components/atoms/Container/Container'
import DynamicSlider from '~/components/atoms/DynamicSlider'
import Image from '~/components/atoms/Image/Image'
import PulsingDot, { DotSize } from '~/components/atoms/PulsingDot/PulsingDot'
import { SliderThemes } from '~/components/atoms/Slider/sliderThemes'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { ComponentTheme } from '~/components/themes'
import { useAuthV2 } from '~/contexts/AuthContext'
import { useMediaQuery } from '~/services/useMediaQuery'
import { multiPostHogEventPusher } from '~/utils/analyticsPusher'
import { getLocalizedHardCodedSlug } from '~/utils/getLocalizedHardCodedSlug'

import styles from './MyPortfolioBanner.module.css'

type MyPortfolioBannerSlideProps = {
  title: string
  body: string
  slideType: 'portfolio' | 'tradefloor' | 'download'
  imageAsset: {
    desktop: string
    mobile: string
  }
  downloadFile?: any
  theme: ComponentTheme
  hasInteracted: boolean
  slideNumber: number
  isMobile?: boolean
}

const MyPortfolioBannerSlide = ({
  title,
  body,
  slideType,
  theme,
  hasInteracted,
  slideNumber,
  imageAsset,
  isMobile,
}: MyPortfolioBannerSlideProps) => {
  const { user } = useAuthV2()
  const t = useTranslations('myPortfolioBanner')
  const tAuth = useTranslations('auth')
  const tCommon = useTranslations('common')
  const tNav = useTranslations('navigation')

  const themes = {
    primary: {
      primaryButton: {
        variant: ButtonVariants.primaryLight,
      },
      secondaryButton: {
        variant: ButtonVariants.secondaryLight,
      },
      imageClass: styles[`slide${slideType}${theme}`],
    },
    secondary: {
      primaryButton: {
        variant: ButtonVariants.primaryDark,
      },
      secondaryButton: {
        variant: ButtonVariants.secondaryDark,
      },
      imageClass: styles[`slide${slideType}${theme}`],
    },
  } as any

  const locale = useLocale()

  const renderButtons = () => {
    if (slideType === 'portfolio') {
      return (
        <>
          {!user ? (
            <>
              <Button
                variant={themes[theme].secondaryButton.variant}
                link={'/login'}
                className={classNames(styles.slideButton, styles.loginButton)}
                linkOnclick={async () => {
                  await multiPostHogEventPusher([
                    {
                      eventType: `MyPortfolio Banner login clicked`,
                    },
                  ])
                }}
                // gaTrack={link.gaTrack}
                // gaTrackEventName={link.gaTrackEventName}
              >
                {tAuth('login')}
              </Button>
              <Button
                variant={themes[theme].primaryButton.variant}
                iconLeft
                link={getLocalizedHardCodedSlug('my-portfolio', locale)}
                className={classNames(styles.myButton, styles.slideButton)}
                linkOnclick={async () => {
                  await multiPostHogEventPusher([
                    {
                      eventType: `MyPortfolio Banner Join MyPortfolio clicked`,
                    },
                  ])
                }}
              >
                {t.rich('joinMyPortfolio', { em: chunks => <em>{chunks}</em> })}
              </Button>
            </>
          ) : (
            <Button
              variant={themes[theme].primaryButton.variant}
              link={'/dashboard'}
              iconLeft
              className={classNames(styles.myButton, styles.slideButton)}
            >
              {tCommon.rich('goToMyPortfolio', {
                em: chunks => <em>{chunks}</em>,
              })}
            </Button>
          )}
        </>
      )
    }
    if (slideType === 'tradefloor') {
      return (
        <>
          <Button
            variant={themes[theme].primaryButton.variant}
            iconLeft
            link={getLocalizedHardCodedSlug('trading-floor', locale)}
            className={classNames(styles.myButton, styles.slideButton)}
            linkOnclick={async () => {
              await multiPostHogEventPusher([
                {
                  eventType: `MyPortfolio Banner Visit Trading Floor clicked`,
                },
              ])
            }}
          >
            {t.rich('visitTradingFloor', { em: chunks => <em>{chunks}</em> })}
          </Button>
        </>
      )
    }
    if (slideType === 'download') {
      return (
        <>
          <Button
            variant={themes[theme].primaryButton.variant}
            iconLeft
            className={styles.slideButton}
            link="/Investing-in-the-print-market-report-contemporary-pop-and-urban-art"
            linkOnclick={async () => {
              await multiPostHogEventPusher([
                {
                  eventType: `MyPortfolio Banner Download now clicked`,
                },
              ])
            }}
          >
            {t('accessReports')}
          </Button>
        </>
      )
    }
  }

  return (
    <>
      <div
        className={classNames(
          styles.slide,
          (hasInteracted || slideNumber === 0) && themes[theme].imageClass,
          styles[theme]
        )}
      >
        <div className={styles.slideMedia}>
          <Image
            asset={!isMobile ? imageAsset.desktop : imageAsset.mobile}
            sizes="100vw"
            alt={title}
            quality={100}
          />
        </div>
        <div
          className={classNames(
            styles.slideContent,
            slideType === 'download' && styles.slideContentDownload
          )}
        >
          <Text
            size={TextSizes.secondaryTitleLrg}
            Tag="h3"
            className={styles.slideTitle}
          >
            {slideType === 'tradefloor' && (
              <div className={styles.tradingDot}>
                <PulsingDot size={DotSize.large} />
              </div>
            )}
            <span dangerouslySetInnerHTML={{ __html: title }} />
          </Text>
          <Text size={TextSizes.bodyLrg} Tag="p" className={styles.slideText}>
            <span dangerouslySetInnerHTML={{ __html: body }} />
          </Text>
          <div
            className={classNames(
              styles.slideButtonContainer,
              styles[`slideButtonContainer${slideType}`]
            )}
          >
            {renderButtons()}
          </div>
        </div>
      </div>
    </>
  )
}

export interface MyPortfolioBannerProps {
  testID?: string
  slide1Title: string
  slide1Body: string
  slide2Title: string
  slide2Body: string
  slide3Title: string
  slide3Body: string
  slide3File?: any
  myPortfolioBannerTheme: ComponentTheme
  isDark?: boolean
}

export default function MyPortfolioBanner({
  slide1Title: slide1Title,
  slide1Body: slide1Body,
  slide2Title: slide2Title,
  slide2Body: slide2Body,
  slide3Title: slide3Title,
  slide3Body: slide3Body,
  slide3File: slide3File,
  testID,
  myPortfolioBannerTheme: theme = ComponentTheme.primary,
  isDark = false,
}: MyPortfolioBannerProps) {
  const isMobile = useMediaQuery(`(max-width: 799px)`)
  const tCommon = useTranslations('common')
  const tNavMag = useTranslations('navigation.magazineTab')
  const locale = useLocale()

  const [hasInteracted, setHasInteracted] = useState<boolean>(false)

  const slides = [
    {
      title: slide1Title,
      body: slide1Body,
      slideType: 'portfolio',
      theme: theme,
      imageAsset: {
        desktop: isDark
          ? 'image-2c6241dd1adb5717f076f8f8b4b0b7cc9c533188-3024x1244-png'
          : 'image-190b441fb1c16472402b26bd7c827f98d8caa2d3-3024x1244-png',
        mobile: isDark
          ? 'image-ab30ede41d2e8658fc33a38d7b935ff0be43f0a1-2484x1368-png'
          : 'image-52f75bf023bc6ec26bef8ad2a6a1205333f3375b-2484x1368-png',
      },
    },
    {
      title: slide2Title,
      body: slide2Body,
      slideType: 'tradefloor',
      theme: theme,
      imageAsset: {
        desktop: isDark
          ? 'image-109fd0cea56bed58ace71b76830c1495ad9cde7a-3024x1244-png'
          : 'image-1493c744c8df8b1b5e0aada7580ec414aefb89ad-3024x1244-png',
        mobile: isDark
          ? 'image-f934db5519ed8cf4c3a571852b18964e50f36085-2484x1698-png'
          : 'image-d53bfa64f25a84e5f2d0705b3675204f800b114e-2484x1698-png',
      },
    },
  ] as MyPortfolioBannerSlideProps[]

  if (locale === 'en') {
    slides.push({
      title: slide3Title,
      body: slide3Body,
      slideType: 'download',
      theme: theme,
      imageAsset: {
        desktop: isDark
          ? 'image-8ca76429644816633669b64c7b3af34120215caa-3024x1244-png'
          : 'image-8fe685a7ef02dd3c8dea45a61ab3e25ee3eebf8f-3024x1244-png',

        mobile: isDark
          ? 'image-ffea08e2980cec926826f5b5122f9acf52915dd2-2484x1374-png'
          : 'image-11f9873d9be250355d18348eb9e0dd94f184cc5a-2484x1362-png',
      },
    } as MyPortfolioBannerSlideProps)
  }

  return (
    <>
      <div
        className={classNames(styles.base, styles[theme])}
        data-testid={testID}
      >
        <Container
          gutters={isMobile ? gutterTypes.never : undefined}
          className={
            theme === ComponentTheme.primary
              ? styles.containerDark
              : styles.containerLight
          }
        >
          <DynamicSlider
            slidesPerView={1}
            slidesPerViewMed={1}
            slidesPerViewLrg={1}
            slidesPerViewHuge={1}
            overflowVisible={false}
            onSlideChange={() => setHasInteracted(true)}
            spaceBetween={0}
            hideArrowsMobile
            sliderKey={`portfolio-banner-${isMobile ? 'mobile' : 'desktop'}`}
            spacer={false}
            theme={
              theme === ComponentTheme.primary
                ? SliderThemes.dark
                : SliderThemes.light
            }
            useScrollbar={isMobile}
            wrapperClass={styles.sliderWrapperClass}
            pagination={!isMobile}
            customPaginationTitles={[
              tCommon.rich('myPortfolio', { em: chunks => <em>{chunks}</em> }),
              tCommon.rich('liveTrading', { em: chunks => <em>{chunks}</em> }),
              tNavMag('printMarketReports'),
            ]}
            customControls={styles.customControls}
            customScrollbarClass={{
              horizontalClass:
                theme === ComponentTheme.primary
                  ? styles.sliderHorizontalClassDark
                  : styles.sliderHorizontalClass,
              dragClass:
                theme === ComponentTheme.primary
                  ? styles.sliderDragClassDark
                  : styles.sliderDragClass,
            }}
          >
            {slides.map((slide, key) => (
              <MyPortfolioBannerSlide
                key={`slide-item-desktop-${key}`}
                {...slide}
                hasInteracted={hasInteracted}
                slideNumber={key}
                isMobile={isMobile}
              />
            ))}
          </DynamicSlider>
        </Container>
      </div>
    </>
  )
}
